import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  FISHSIX_SURVEY_SECTION_DISPLAY,
  FISHSIX_SURVEY_ADMIN_OPTIONS,
  FISHSIX_SURVEY_FACILITY_OPTIONS,
  FISHSIX_SURVEY_LEARNING_OPTIONS,
  FISHSIX_SURVEY_TEACHER_OPTIONS,
  type FishsixSurveyResponsesV1,
  type FishSurveyOption,
} from "@/utils/fishsixSurveyConstants";
import { fishsixOptionLabel } from "@/utils/fishsixSurveyAdmin";

function ScoreBlock({ label, value }: { label: string; value: number }) {
  const valid = value >= 1 && value <= 10;
  const pct = valid ? (value / 10) * 100 : 0;
  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <p className="text-sm leading-snug text-muted-foreground">{label}</p>
        <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
          {valid ? `${value}/10` : "—"}
        </span>
      </div>
      {valid ? <Progress value={pct} className="h-2" /> : null}
    </div>
  );
}

function LabeledChoices({
  heading,
  ids,
  options,
}: {
  heading: string;
  ids: string[];
  options: FishSurveyOption[];
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">{heading}</p>
      {!ids.length ? (
        <p className="text-sm text-muted-foreground">ไม่ได้ระบุ</p>
      ) : (
        <ul className="space-y-2">
          {ids.map((id) => (
            <li
              key={id}
              className="rounded-md border border-border/80 bg-muted/30 px-3 py-2 text-sm leading-snug text-foreground"
            >
              {fishsixOptionLabel(id, options)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SectionShell({
  kicker,
  title,
  children,
}: {
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-muted shadow-none">
      <CardHeader className="space-y-1 pb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{kicker}</p>
        <CardTitle className="text-lg font-semibold leading-tight">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 pt-0">{children}</CardContent>
    </Card>
  );
}

export interface FishsixParentReviewDetailProps {
  parsed: FishsixSurveyResponsesV1;
  /** Limit which sections to render. `general` hides s2 (teacher); `teacher` shows only s2. */
  scope?: "all" | "general" | "teacher";
}

export const FishsixParentReviewDetail: React.FC<FishsixParentReviewDetailProps> = ({ parsed, scope = "all" }) => {
  const copy = FISHSIX_SURVEY_SECTION_DISPLAY;
  const showGeneral = scope === "all" || scope === "general";
  const showTeacher = scope === "all" || scope === "teacher";

  return (
    <div className="mt-6 space-y-6 pb-6">
      {showGeneral ? (
      <SectionShell kicker={copy.s1.kicker} title={copy.s1.title}>
        <ScoreBlock label={copy.s1.academic} value={parsed.s1_academic_development} />
        <Separator />
        <LabeledChoices heading={copy.s1.clearOutcomes} ids={parsed.s1_clear_outcomes} options={FISHSIX_SURVEY_LEARNING_OPTIONS} />
        <LabeledChoices heading={copy.s1.emphasizeMore} ids={parsed.s1_emphasize_more} options={FISHSIX_SURVEY_LEARNING_OPTIONS} />
      </SectionShell>) : null}

      {showTeacher ? (
      <SectionShell kicker={copy.s2.kicker} title={copy.s2.title}>
        <ScoreBlock label={copy.s2.teacherQuality} value={parsed.s2_teacher_quality} />
        <Separator />
        <LabeledChoices heading={copy.s2.strengths} ids={parsed.s2_teacher_strengths} options={FISHSIX_SURVEY_TEACHER_OPTIONS} />
        <LabeledChoices heading={copy.s2.improve} ids={parsed.s2_teacher_improve} options={FISHSIX_SURVEY_TEACHER_OPTIONS} />
      </SectionShell>) : null}

      {showGeneral ? (
      <SectionShell kicker={copy.s3.kicker} title={copy.s3.title}>
        <ScoreBlock label={copy.s3.adminService} value={parsed.s3_admin_service} />
        <Separator />
        <LabeledChoices heading={copy.s3.strengths} ids={parsed.s3_admin_strengths} options={FISHSIX_SURVEY_ADMIN_OPTIONS} />
        <LabeledChoices heading={copy.s3.improve} ids={parsed.s3_admin_improve} options={FISHSIX_SURVEY_ADMIN_OPTIONS} />
      </SectionShell>) : null}

      {showGeneral ? (
      <SectionShell kicker={copy.s4.kicker} title={copy.s4.title}>
        <ScoreBlock label={copy.s4.facilityAtmosphere} value={parsed.s4_facility_atmosphere} />
        <Separator />
        <LabeledChoices heading={copy.s4.strengths} ids={parsed.s4_facility_strengths} options={FISHSIX_SURVEY_FACILITY_OPTIONS} />
        <LabeledChoices heading={copy.s4.improve} ids={parsed.s4_facility_improve} options={FISHSIX_SURVEY_FACILITY_OPTIONS} />
      </SectionShell>) : null}

      {showGeneral ? (
      <SectionShell kicker={copy.s5.kicker} title={copy.s5.title}>
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{copy.s5.prompt}</p>
          {parsed.s5_additional.trim() ? (
            <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-3 text-sm leading-relaxed whitespace-pre-wrap">
              {parsed.s5_additional}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">ไม่ได้ระบุ</p>
          )}
        </div>
      </SectionShell>) : null}
    </div>
  );
};
